if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Arrows switch which media is .is-active. Bound before the thumbnail
        // early-return so they still work on galleries without a thumbnail strip.
        this.querySelectorAll('[data-gallery-arrow]').forEach((arrow) => {
          arrow.addEventListener('click', () => this.stepMedia(arrow.dataset.galleryArrow === 'next' ? 1 : -1));
        });

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        // Delegated, so thumbnails re-rendered on a variant change stay clickable.
        // (Stock Dawn bound each button once in the constructor.)
        this.elements.thumbnails.addEventListener('click', (event) => {
          const target = event.target.closest('[data-target]');
          if (!target || !this.elements.thumbnails.contains(target)) return;
          this.setActiveMedia(target.dataset.target, false);
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const current = event.detail?.currentElement;
        if (!current || !this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${current.dataset.mediaId}"]`
        );
        if (thumbnail) this.setActiveThumbnail(thumbnail);
      }

      // The slide <li>s only. NOT a bare [data-media-id] lookup: .product__media-toggle
      // carries data-media-id="{{ media.id }}" too, so a loose selector returns
      // twice as many nodes and stepping lands on a child of the current slide.
      slideElements() {
        return Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
      }

      // Move n places from the currently active media, clamped at both ends.
      stepMedia(n) {
        const slides = this.slideElements();
        if (slides.length < 2) return;
        const current = slides.findIndex((el) => el.classList.contains('is-active'));
        const next = Math.min(Math.max((current === -1 ? 0 : current) + n, 0), slides.length - 1);
        if (next === current) return;
        this.setActiveMedia(slides[next].dataset.mediaId, false);
        this.updateArrows();
      }

      // Disable whichever arrow would run off the end.
      updateArrows() {
        const arrows = this.querySelectorAll('[data-gallery-arrow]');
        if (!arrows.length) return;
        const slides = this.slideElements();
        const current = slides.findIndex((el) => el.classList.contains('is-active'));
        arrows.forEach((arrow) => {
          const isNext = arrow.dataset.galleryArrow === 'next';
          const atEnd = isNext ? current >= slides.length - 1 : current <= 0;
          arrow.toggleAttribute('disabled', slides.length < 2 || atEnd);
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            // May be absent: the thumbnail list can lag a variant change.
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
